/**
 * Autonomous Development Mode
 * One-click complete app development
 */

import { useState } from 'react';
import { getAutonomousOrchestrator, AUTONOMOUS_MODES } from '../lib/autonomous-orchestrator';

export function AutonomousMode() {
  const [orchestrator] = useState(() => getAutonomousOrchestrator({
    mode: AUTONOMOUS_MODES.FULL_AUTO,
    autoDeployEnabled: false // User can enable later
  }));

  const [idea, setIdea] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);

  // Config
  const [config, setConfig] = useState({
    autoFix: true,
    autoReview: true,
    autoDeploy: false,
    continuousImprovement: true
  });

  const handleCreate = async () => {
    if (!idea.trim()) return;

    setIsRunning(true);
    setProgress('🚀 Starting autonomous development...');
    setResult(null);

    try {
      // Update orchestrator config
      orchestrator.autoFixEnabled = config.autoFix;
      orchestrator.autoReviewEnabled = config.autoReview;
      orchestrator.autoDeployEnabled = config.autoDeploy;
      orchestrator.continuousImprovementEnabled = config.continuousImprovement;

      // Create complete app
      const appResult = await orchestrator.createCompleteApp(idea, {
        onProgress: (msg) => setProgress(msg),
        onPhaseComplete: (phase) => setProgress(`✅ ${phase} complete`)
      });

      setResult(appResult);
      setCurrentProject(appResult.projectId);
      setProgress('🎉 App created successfully!');

    } catch (error) {
      setProgress(`❌ Error: ${error.message}`);
      setResult({ success: false, error: error.message });
    } finally {
      setIsRunning(false);
    }
  };

  const activeProjects = orchestrator.getAllProjects();

  return (
    <div className="autonomous-mode">
      <div className="autonomous-header">
        <h1>🤖 Autonomous Development Mode</h1>
        <p className="subtitle">Describe your app idea - I'll build it completely, automatically</p>
      </div>

      {/* Main Input */}
      <div className="create-app-section">
        <div className="idea-input-container">
          <textarea
            className="idea-input"
            placeholder="Describe your app idea...&#10;&#10;Examples:&#10;• &quot;Create a todo app with user authentication and cloud sync&quot;&#10;• &quot;Build an e-commerce site with product catalog and checkout&quot;&#10;• &quot;Make a real-time chat application with rooms and presence&quot;&#10;&#10;I'll automatically:&#10;✓ Analyze requirements&#10;✓ Design architecture&#10;✓ Implement frontend + backend + database&#10;✓ Create comprehensive tests&#10;✓ Review and optimize code&#10;✓ Fix all issues&#10;✓ Deploy (optional)"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            rows={8}
            disabled={isRunning}
          />

          <button
            className="create-btn"
            onClick={handleCreate}
            disabled={!idea.trim() || isRunning}
          >
            {isRunning ? '⚙️ Creating App...' : '🚀 Create Complete App'}
          </button>
        </div>

        {/* Configuration */}
        <div className="config-section">
          <h3>⚙️ Autonomous Settings</h3>
          <div className="config-options">
            <label className="config-option">
              <input
                type="checkbox"
                checked={config.autoFix}
                onChange={(e) => setConfig({ ...config, autoFix: e.target.checked })}
              />
              <span className="option-label">
                <strong>Auto-Fix Issues</strong>
                <small>Automatically fix bugs, test failures, and review issues</small>
              </span>
            </label>

            <label className="config-option">
              <input
                type="checkbox"
                checked={config.autoReview}
                onChange={(e) => setConfig({ ...config, autoReview: e.target.checked })}
              />
              <span className="option-label">
                <strong>Auto-Review Code</strong>
                <small>Automatically review code for security, performance, and quality</small>
              </span>
            </label>

            <label className="config-option">
              <input
                type="checkbox"
                checked={config.autoDeploy}
                onChange={(e) => setConfig({ ...config, autoDeploy: e.target.checked })}
              />
              <span className="option-label">
                <strong>Auto-Deploy</strong>
                <small>Automatically deploy to production after all checks pass</small>
              </span>
            </label>

            <label className="config-option">
              <input
                type="checkbox"
                checked={config.continuousImprovement}
                onChange={(e) => setConfig({ ...config, continuousImprovement: e.target.checked })}
              />
              <span className="option-label">
                <strong>Continuous Improvement</strong>
                <small>Background monitoring and auto-improvement of deployed apps</small>
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Progress */}
      {progress && (
        <div className={`progress-box ${isRunning ? 'active' : ''}`}>
          <div className="progress-text">{progress}</div>
          {isRunning && (
            <div className="progress-spinner">⚙️</div>
          )}
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`result-box ${result.success ? 'success' : 'error'}`}>
          {result.success ? (
            <>
              <h3>🎉 App Created Successfully!</h3>
              <div className="result-stats">
                <div className="stat">
                  <span className="stat-label">Duration:</span>
                  <span className="stat-value">{Math.round(result.duration / 1000)}s</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Files Created:</span>
                  <span className="stat-value">{result.filesCreated}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Tests Created:</span>
                  <span className="stat-value">{result.testsCreated}</span>
                </div>
                {result.deploymentUrl && (
                  <div className="stat">
                    <span className="stat-label">Deployed:</span>
                    <a href={result.deploymentUrl} target="_blank" rel="noopener noreferrer">
                      {result.deploymentUrl}
                    </a>
                  </div>
                )}
              </div>

              {result.summary && (
                <div className="summary">
                  <h4>Summary</h4>
                  <pre>{JSON.stringify(result.summary, null, 2)}</pre>
                </div>
              )}
            </>
          ) : (
            <>
              <h3>❌ Error</h3>
              <p>{result.error}</p>
            </>
          )}
        </div>
      )}

      {/* Active Projects */}
      {activeProjects.length > 0 && (
        <div className="active-projects">
          <h3>📊 Active Projects ({activeProjects.length})</h3>
          <div className="projects-list">
            {activeProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-header">
                  <h4>{project.idea}</h4>
                  <span className={`status-badge ${project.status}`}>
                    {project.status}
                  </span>
                </div>
                <div className="project-meta">
                  <span>Phase: {project.phase}</span>
                  <span>Files: {project.files.length}</span>
                  <span>Issues: {project.issues.length}</span>
                </div>
                {project.deployments.length > 0 && (
                  <div className="project-deployments">
                    🚀 Deployed: <a href={project.deployments[0].url} target="_blank" rel="noopener noreferrer">
                      {project.deployments[0].url}
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features Grid */}
      <div className="features-grid">
        <div className="feature-card">
          <div className="feature-icon">📋</div>
          <h3>Requirements Analysis</h3>
          <p>Automatically extract complete requirements from your idea</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">🎨</div>
          <h3>Architecture Design</h3>
          <p>Design complete system architecture, APIs, and data models</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">⚙️</div>
          <h3>Full Implementation</h3>
          <p>Write all code: frontend, backend, database, tests</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">🧪</div>
          <h3>Comprehensive Testing</h3>
          <p>Create and run unit, integration, and E2E tests</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">👁️</div>
          <h3>Code Review</h3>
          <p>Automatic security, performance, and quality review</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">🔧</div>
          <h3>Auto-Fix Issues</h3>
          <p>Automatically fix bugs, test failures, and review issues</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">⚡</div>
          <h3>Optimization</h3>
          <p>Optimize code for performance and maintainability</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">🚀</div>
          <h3>Auto-Deployment</h3>
          <p>Deploy to Vercel, Railway, or Cloudflare Pages</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">👁️‍🗨️</div>
          <h3>Continuous Monitoring</h3>
          <p>Background monitoring and proactive issue detection</p>
        </div>

        <div className="feature-card">
          <div className="feature-icon">🔄</div>
          <h3>Self-Improvement</h3>
          <p>Continuously improve code quality and performance</p>
        </div>
      </div>
    </div>
  );
}

export default AutonomousMode;
